import { card, el } from '../dom';
import { setPreviewPersonaAndEnter } from '../shell/app-shell';
import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  previewRedirectUri,
  staffLogoutUri,
} from '../staff/authorize';
import { STAFF_DOOR } from '../staff/endpoints';
import { createPkcePair, randomState } from '../staff/pkce';
import {
  STATE_KEY,
  VERIFIER_KEY,
  clearStaffSession,
  exchangeAuthorizationCode,
  loadStaffSessionPublic,
  markStaffSignedOut,
  scrubOAuthQueryFromUrl,
  type StaffSessionPublic,
} from '../staff/token';

function seedPreviewEnabled(): boolean {
  return new URLSearchParams(window.location.search).get('seed') === '1';
}

function brandLockup(): HTMLElement {
  return el('img', {
    class: 'door-page__lockup-img',
    src: '/brand/logo-lockup-page.png',
    alt: 'Dubbed',
  });
}

function copyrightFooter(): HTMLElement {
  return el('p', { class: 'door-page__copy' }, '©2026 Dubbed, LLC.');
}

/** Full-bleed D6W6 door — lockup + Continue with Google + © footer. */
export function renderStaff(mount: HTMLElement): void {
  const session = loadStaffSessionPublic();
  if (session) {
    const enter = el(
      'button',
      { class: 'door-page__google', type: 'button' },
      'Open staff calendar',
    );
    enter.addEventListener('click', () => {
      setPreviewPersonaAndEnter('admin');
    });
    const kids: HTMLElement[] = [
      brandLockup(),
      el('p', { class: 'door-page__status' }, `Signed in as ${session.email ?? 'staff'}`),
      enter,
      signOutLink(),
    ];
    if (seedPreviewEnabled()) {
      kids.push(seedHint());
    }
    mount.append(
      el(
        'section',
        { class: 'door-page' },
        el('div', { class: 'door-page__center' }, ...kids),
        copyrightFooter(),
      ),
    );
    return;
  }

  const google = el(
    'button',
    { class: 'door-page__google', type: 'button' },
    'Continue with Google',
  );
  google.addEventListener('click', () => {
    void startAuthorize('Google');
  });

  const centerKids: HTMLElement[] = [brandLockup(), google];
  if (seedPreviewEnabled()) {
    centerKids.push(seedHint());
    centerKids.push(previewEnterButton('admin', 'Open as admin'));
    centerKids.push(previewEnterButton('owner', 'Open as location owner'));
  }

  mount.append(
    el(
      'section',
      { class: 'door-page', 'aria-label': 'Sign in' },
      el('div', { class: 'door-page__center' }, ...centerKids),
      copyrightFooter(),
    ),
  );
}

export function renderBreakGlass(mount: HTMLElement): void {
  const session = loadStaffSessionPublic();
  if (session) {
    renderStaff(mount);
    return;
  }

  const password = el(
    'button',
    { class: 'door-page__google', type: 'button' },
    'Sign in with password',
  );
  password.addEventListener('click', () => {
    void startAuthorize('COGNITO');
  });

  mount.append(
    el(
      'section',
      { class: 'door-page', 'aria-label': 'Break-glass sign-in' },
      el(
        'div',
        { class: 'door-page__center' },
        brandLockup(),
        el('p', { class: 'door-page__status' }, 'Break-glass password sign-in'),
        password,
      ),
      copyrightFooter(),
    ),
  );
}

function seedHint(): HTMLElement {
  return el('p', { class: 'door-page__status' }, 'Seed preview (?seed=1)');
}

function previewEnterButton(persona: 'admin' | 'owner', label: string): HTMLButtonElement {
  const button = el('button', { class: 'door-page__google', type: 'button' }, label);
  button.addEventListener('click', () => {
    setPreviewPersonaAndEnter(persona);
  });
  return button;
}

async function startAuthorize(identityProvider: 'Google' | 'COGNITO'): Promise<void> {
  try {
    const { verifier, challenge } = await createPkcePair();
    const state = randomState();
    sessionStorage.setItem(VERIFIER_KEY, verifier);
    sessionStorage.setItem(STATE_KEY, state);

    const url = buildAuthorizeUrl({
      clientId: STAFF_DOOR.clientId,
      redirectUri: previewRedirectUri(),
      state,
      challenge,
      identityProvider,
    });

    window.location.assign(url);
  } catch (error) {
    window.alert(error instanceof Error ? error.message : 'Could not start sign-in.');
  }
}

function signOutLink(): HTMLElement {
  const button = el('button', { class: 'door-page__password', type: 'button' }, 'Sign out');
  button.addEventListener('click', () => {
    clearStaffSession();
    markStaffSignedOut();
    window.location.assign(
      buildLogoutUrl({
        clientId: STAFF_DOOR.clientId,
        logoutUri: staffLogoutUri(),
      }),
    );
  });
  return button;
}

function signedInCard(session: StaffSessionPublic) {
  const staffOk = session.kind === 'staff';
  return card({
    title: staffOk ? 'Signed in — staff' : 'Signed in — unexpected kind',
    tone: staffOk ? 'live' : 'blocked',
    toneLabel: staffOk ? 'staff' : session.kind,
    body: 'Token exchange succeeded. Entering calendar — tokens are not displayed.',
    facts: [
      ['email', session.email || '(missing)'],
      ['kind', session.kind],
      ['staff.session scope', session.hasStaffSession ? 'present' : 'absent'],
    ],
  });
}

export function renderStaffCallback(mount: HTMLElement): void {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const codePresent = Boolean(code);
  const oauthError = params.get('error');
  const state = params.get('state');
  const expected = sessionStorage.getItem(STATE_KEY);
  const stateOk = Boolean(state && expected && state === expected);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  const statusHost = el('div', { class: 'door-page__center', 'data-slot': 'callback-status' });
  mount.append(
    el(
      'section',
      { class: 'door-page' },
      statusHost,
      copyrightFooter(),
    ),
  );

  statusHost.append(
    el('p', { class: 'door-page__status', 'data-slot': 'callback-lede' }, 'Signing in…'),
  );

  void runTokenExchange({
    mount: statusHost,
    lede: statusHost.querySelector('[data-slot="callback-lede"]') as HTMLElement | null,
    code,
    codePresent,
    oauthError,
    stateOk,
    verifier,
  });
}

async function runTokenExchange(args: {
  readonly mount: HTMLElement;
  readonly lede: HTMLElement | null;
  readonly code: string | null;
  readonly codePresent: boolean;
  readonly oauthError: string | null;
  readonly stateOk: boolean;
  readonly verifier: string | null;
}): Promise<void> {
  const fail = (title: string, body: string) => {
    if (args.lede) args.lede.textContent = title;
    args.mount.append(el('p', { class: 'door-page__status' }, body));
    scrubOAuthQueryFromUrl();
  };

  if (args.oauthError) {
    fail('Sign-in failed', args.oauthError);
    return;
  }
  if (!args.codePresent || !args.code) {
    fail('No authorization code', 'Open the door and Continue with Google.');
    return;
  }
  if (!args.stateOk) {
    fail('State mismatch', 'Start sign-in again from this origin.');
    return;
  }
  if (!args.verifier) {
    fail('Missing PKCE verifier', 'Start sign-in from this same browser tab.');
    return;
  }

  try {
    const session = await exchangeAuthorizationCode({
      code: args.code,
      verifier: args.verifier,
      redirectUri: previewRedirectUri(),
    });
    scrubOAuthQueryFromUrl();
    if (args.lede) {
      args.lede.textContent = 'Signed in — opening calendar…';
    }
    args.mount.append(signedInCard(session));
    setPreviewPersonaAndEnter('admin');
  } catch (error) {
    scrubOAuthQueryFromUrl();
    const message = error instanceof Error ? error.message : 'Token exchange failed';
    fail('Token exchange failed', message);
  }
}

export function oauthCallbackPending(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has('code') || params.has('error');
}
